import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileDown, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { getComposition } from '../data/mock';
import { useFluoVehicles } from '../hooks/useFluoVehicles';
import { useGrandEstStations } from '../hooks/useGrandEstStations';

const STATUS_LABELS = (t) => ({
  on_time: t('board.status_ontime'),
  delayed: t('board.status_delayed'),
  cancelled: t('board.status_cancelled'),
});

/** Replace Unicode characters that jsPDF's default Helvetica/WinAnsi font
 *  cannot render correctly (they would otherwise show up as "&", "Ø" or
 *  similar garbage). Keep ASCII + common accented Latin chars as-is. */
const pdfSafe = (s) => {
  if (s == null) return '';
  return String(s)
    .replace(/→/g, '>')
    .replace(/←/g, '<')
    .replace(/↔/g, '<>')
    .replace(/—/g, '-')   // em-dash
    .replace(/–/g, '-')   // en-dash
    .replace(/•/g, '-')   // bullet
    .replace(/·/g, '-')   // middle dot
    .replace(/…/g, '...')
    .replace(/’/g, "'")
    .replace(/‘/g, "'")
    .replace(/“/g, '"')
    .replace(/”/g, '"')
    .replace(/[^\x00-\xff]/g, '?');
};

/** Load a PNG from /assets and return { dataUrl, width, height } so jsPDF
 *  can embed it while preserving the aspect ratio. */
const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL('image/png'),
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    } catch (e) { reject(e); }
  };
  img.onerror = reject;
  img.src = src;
});

/** Generate a PDF of the current board (departures or arrivals) using jsPDF. */
const ExportPdfButton = ({
  stationName,
  mode,
  list,
  sourceLabel,
  commute,
  returnTrains,
  returnEnabled,
}) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const { isFluoCar, getFluoCarSeats } = useFluoVehicles();
  const { isGrandEst, loaded: grandEstLoaded } = useGrandEstStations();

  const onExport = async () => {
    if (!list || list.length === 0) return;
    setBusy(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');

      // Preload footer logos so we can stamp them on every page.
      let sncfImg = null;
      let fluoImg = null;
      try {
        [sncfImg, fluoImg] = await Promise.all([
          loadImage('/assets/sncf_voyageurs.png'),
          loadImage('/assets/fluo_grand_est.png'),
        ]);
      } catch { /* logos missing — fallback to text-only footer */ }

      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const stamp = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;

      // ---- Header band ----
      doc.setFillColor(25, 25, 152); // ter-blue #191998
      doc.rect(0, 0, pageW, 70, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text('Fluo Grand Est', 40, 32);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text('par SNCF Voyageurs', 40, 48);

      // Yellow accent band
      doc.setFillColor(255, 205, 0); // ter-yellow
      doc.rect(0, 70, pageW, 4, 'F');

      // ---- Title block ----
      doc.setTextColor(25, 25, 152);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      const titleLabel = commute
        ? `${t('commute.eyebrow')} - ${commute.originName} > ${commute.destName}`
        : (mode === 'departures' ? t('board.tab_dep') : t('board.tab_arr')) + ` - ${stationName}`;
      doc.text(pdfSafe(titleLabel), 40, 100);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(pdfSafe(`${t('export.generated_at')} ${stamp}`), 40, 116);
      if (sourceLabel) doc.text(pdfSafe(sourceLabel), 40, 128);

      const labels = STATUS_LABELS(t);

      // ---- Helpers to render a single train section ----
      const buildHead = (isCommute, sectionMode) => {
        if (isCommute) {
          return [[
            t('commute.from'),
            t('commute.to'),
            t('board.col_train'),
            t('board.col_coaches'),
            t('board.col_seats'),
            t('board.col_platform'),
            t('board.col_status'),
          ].map(pdfSafe)];
        }
        return [(sectionMode === 'departures'
          ? [t('board.col_time'), t('board.col_train'), t('board.col_destination'), t('board.col_coaches'), t('board.col_seats'), t('board.col_platform'), t('board.col_status')]
          : [t('board.col_time'), t('board.col_train'), t('board.col_origin'), t('board.col_coaches'), t('board.col_seats'), t('board.col_platform'), t('board.col_status')]
        ).map(pdfSafe)];
      };

      const buildBody = (rows, isCommute, sectionMode) => rows.map((tr) => {
        const time = tr.delay > 0 ? `${tr.time} (+${tr.delay} > ${tr.realTime})` : tr.time;
        const isCar = isFluoCar(tr.number);
        const getTrainLabel = () => {
          if (isCar) return 'Bus Fluo';
          const marque = tr.marque || '';
          const type   = tr.type   || '';
          const mode   = tr.modeTransport || '';
          if (marque && marque.toUpperCase() !== 'TER') return marque;
          if (mode === 'highSpeedRail') return 'TGV';
          if (type === 'IC') return 'Intercités';
          if (type === 'TER' || marque.toUpperCase() === 'TER') {
            const originFallback = tr.origin || commute?.originName || stationName || '';
            const inGrandEst = isGrandEst(originFallback) || isGrandEst(tr.destination || '');
            return inGrandEst ? 'Train Fluo' : 'TER';
          }
          return marque || 'Train';
        };
        const trainCol = `${getTrainLabel()} ${tr.number}`;
        const comp = tr.status === 'cancelled'
          ? null
          : isCar
            ? { wagonsCount: 1, totalSeats: getFluoCarSeats(tr.number) }
            : getComposition(tr.number);
        const coaches = isCar ? '1 bus' : (comp ? String(comp.wagonsCount) : '-');
        const seats = comp ? String(comp.totalSeats) : '-';
        const platform = (isCar && !tr.platform) ? 'Bus' : (tr.platform && tr.platform !== '—' ? tr.platform : '-');
        const status = labels[tr.status] || tr.status;
        const row = isCommute
          ? [time, tr.arrival_at_dest && tr.arrival_at_dest !== '—' ? tr.arrival_at_dest : '-', trainCol, coaches, seats, platform, status]
          : [time, trainCol, (sectionMode === 'departures' ? (tr.destination || '-') : (tr.origin || '-')), coaches, seats, platform, status];
        return row.map(pdfSafe);
      });

      const renderSection = (rows, isCommute, sectionMode, startY) => {
        autoTable(doc, {
          startY,
          head: buildHead(isCommute, sectionMode),
          body: buildBody(rows, isCommute, sectionMode),
          margin: { left: 40, right: 40, bottom: 80 }, // reserve footer space
          styles: { font: 'helvetica', fontSize: 10, cellPadding: 6, textColor: [30, 41, 59] },
          headStyles: { fillColor: [25, 25, 152], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          columnStyles: {
            0: { cellWidth: 95, fontStyle: 'bold' },
            1: { cellWidth: 75 },
            3: { cellWidth: 50, halign: 'center' },
            4: { cellWidth: 55, halign: 'center' },
            5: { cellWidth: 50, halign: 'center' },
            6: { cellWidth: 75, halign: 'center' },
          },
          didParseCell: (data) => {
            if (data.section !== 'body') return;
            const row = rows[data.row.index];
            if (!row) return;
            if (data.column.index === 6) {
              if (row.status === 'cancelled') data.cell.styles.textColor = [226, 0, 26];
              else if (row.status === 'delayed') data.cell.styles.textColor = [180, 83, 9];
              else data.cell.styles.textColor = [4, 120, 87];
              data.cell.styles.fontStyle = 'bold';
            }
          },
        });
      };

      const sectionLabel = (text, fromName, toName, y) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(226, 0, 26); // ter-red eyebrow
        doc.text(pdfSafe(text.toUpperCase()), 40, y);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(25, 25, 152);
        doc.text(pdfSafe(`${fromName} > ${toName}`), 40, y + 14);
      };

      // ---- Outbound section ----
      if (commute) {
        sectionLabel(t('export.section_outbound'), commute.originName, commute.destName, 152);
        renderSection(list, true, 'departures', 174);
      } else {
        renderSection(list, false, mode, 144);
      }

      // ---- Return section (commute only) ----
      const hasReturn = commute && returnEnabled && Array.isArray(returnTrains) && returnTrains.length > 0;
      if (hasReturn) {
        const lastY = doc.lastAutoTable?.finalY || 144;
        const remaining = pageH - 80 /* footer */ - lastY;
        let returnLabelY;
        if (remaining < 120) {
          doc.addPage();
          returnLabelY = 60;
        } else {
          returnLabelY = lastY + 32;
        }
        sectionLabel(t('export.section_return'), commute.destName, commute.originName, returnLabelY);
        renderSection(returnTrains, true, 'departures', returnLabelY + 22);
      }

      // ---- Footer on every page ----
      const drawFooter = () => {
        const footerTop = pageH - 64;

        // Thin yellow brand band
        doc.setFillColor(255, 205, 0);
        doc.rect(0, footerTop, pageW, 2, 'F');

        // Logos (height ~24pt) — placed bottom-left
        const logoH = 22;
        let cursorX = 40;
        const logoY = pageH - 48;
        if (sncfImg) {
          const w = (sncfImg.width / sncfImg.height) * logoH;
          doc.addImage(sncfImg.dataUrl, 'PNG', cursorX, logoY, w, logoH);
          cursorX += w + 10;
        }
        if (fluoImg) {
          const w = (fluoImg.width / fluoImg.height) * logoH;
          doc.addImage(fluoImg.dataUrl, 'PNG', cursorX, logoY, w, logoH);
          cursorX += w + 10;
        }

        // Footer text — right of the logos
        const textX = cursorX + 6;
        const textW = pageW - textX - 40;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(100, 116, 139);
        const desc = pdfSafe(t('export.footer_text'));
        doc.text(desc, textX, logoY + 6, { maxWidth: textW, lineHeightFactor: 1.25 });
        doc.setFontSize(7);
        doc.setTextColor(148, 163, 184);
        doc.text(pdfSafe(t('export.footer_copyright')), textX, pageH - 14, { maxWidth: textW });
      };

      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        drawFooter();
      }

      const baseFilename = commute
        ? `fluo-trajet-${commute.originName}-${commute.destName}${hasReturn ? '-aller-retour' : ''}-${stamp.replace(/[/:\s]/g, '-')}`
        : `fluo-${mode}-${stationName}-${stamp.replace(/[/:\s]/g, '-')}`;
      const filename = `${baseFilename}.pdf`
        .replace(/[^\w.-]+/g, '-')
        .toLowerCase();
      doc.save(filename);
    } finally {
      setBusy(false);
    }
  };

  const disabled = !list || list.length === 0 || busy || !grandEstLoaded;

  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={onExport}
      disabled={disabled}
      data-testid={commute ? 'export-pdf-btn-commute' : 'export-pdf-btn'}
      className={
        commute
          ? 'border-0 disabled:opacity-50'
          : 'bg-white/10 text-white hover:bg-white/20 border-0 disabled:opacity-50'
      }
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1" />}
      {t('export.pdf')}
    </Button>
  );
};

export default ExportPdfButton;
